// ============================================================================
// Org lookup — the Prest0 Inc. cross-org activity viewer's picker (R23,
// 2026-08-20 Miles). Rendered ONLY for owners/admins of Prest0 Inc. (the page
// gates on the server's own answer — see Activity.tsx); picking an org
// switches THIS PAGE's data view, never the portal workspace.
//
// SCALE (R23.2, 2026-08-21 Miles: "there's going to be a lot of organizations
// so there needs to be a search functionality"). The list is EVERY org on the
// platform, so this is built as a search-first control, not a menu that
// happens to have a filter:
//   • the search box is always present and focused on open — type immediately;
//   • matching is name OR account id, so a half-remembered either one finds it;
//   • the count is always stated ("48 organizations" / "3 of 48"), so a narrow
//     search never looks like a short list;
//   • rows are capped per render with an explicit "keep typing" tail — a
//     thousand orgs cannot stall the panel;
//   • ↑/↓/Enter drive it from the keyboard without touching the mouse.
// Visually still a sibling of ActivityScopeSelector.
// ============================================================================

import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { ACTIVITY_COLORS } from './activityTheme';
import type { ActivityOrganizationRow } from '../../services/rbacService';

const Wrap = styled.div`
  position: relative;
  display: inline-flex;
`;

const TriggerChip = styled.button<{ $open: boolean; $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.75rem;
  color: ${ACTIVITY_COLORS.ink};
  background: ${(p) =>
    p.$active
      ? 'rgba(198, 167, 94, 0.16)'
      : p.$open
        ? ACTIVITY_COLORS.gridline
        : ACTIVITY_COLORS.surface};
  border: 1px solid ${(p) => (p.$active ? ACTIVITY_COLORS.magnitude : ACTIVITY_COLORS.border)};
  border-radius: 999px;
  padding: 0.375rem 0.875rem;
  cursor: pointer;
  text-transform: lowercase;
  max-width: min(60vw, 280px);
  transition: background 0.15s ease;

  &:hover {
    background: ${(p) => (p.$active ? 'rgba(198, 167, 94, 0.2)' : ACTIVITY_COLORS.gridline)};
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const Caret = styled.span<{ $open: boolean }>`
  flex: none;
  font-size: 0.5625rem;
  color: ${ACTIVITY_COLORS.inkSecondary};
  transform: rotate(${(p) => (p.$open ? 180 : 0)}deg);
  transition: transform 0.15s ease;
`;

const Panel = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 60;
  width: min(340px, calc(100vw - 2rem));
  max-height: min(440px, 70vh);
  overflow-y: auto;
  background: ${ACTIVITY_COLORS.surface};
  border: 1px solid ${ACTIVITY_COLORS.border};
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  padding: 0.375rem;

  @media (max-width: 480px) {
    position: fixed;
    top: auto;
    left: 1rem;
    right: 1rem;
    bottom: 1rem;
    width: auto;
    max-height: 60vh;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  border: 1px solid ${ACTIVITY_COLORS.border};
  border-radius: 6px;
  padding: 0.4375rem 0.625rem;
  font-size: 0.8125rem;
  margin-bottom: 0.375rem;
  color: ${ACTIVITY_COLORS.ink};
  background: ${ACTIVITY_COLORS.surface};

  &:focus {
    outline: none;
    border-color: ${ACTIVITY_COLORS.magnitude};
  }

  &::placeholder {
    color: ${ACTIVITY_COLORS.inkMuted};
  }
`;

const CountNote = styled.div`
  padding: 0 0.5rem 0.375rem;
  font-size: 0.6875rem;
  color: ${ACTIVITY_COLORS.inkMuted};
`;

const Row = styled.button<{ $selected: boolean; $cursor?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  width: 100%;
  min-height: 40px;
  border: none;
  border-radius: 7px;
  background: ${(p) =>
    p.$selected
      ? 'rgba(198, 167, 94, 0.16)'
      : p.$cursor
        ? ACTIVITY_COLORS.gridline
        : 'transparent'};
  color: ${ACTIVITY_COLORS.ink};
  font-size: 0.8125rem;
  text-align: left;
  cursor: pointer;
  padding: 0.375rem 0.5rem;

  &:hover {
    background: ${(p) => (p.$selected ? 'rgba(198, 167, 94, 0.2)' : ACTIVITY_COLORS.gridline)};
  }
`;

const RowLabel = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const QuietNote = styled.div`
  padding: 0.5rem;
  font-size: 0.75rem;
  color: ${ACTIVITY_COLORS.inkMuted};
`;

interface Props {
  organizations: ActivityOrganizationRow[];
  /** the org currently being viewed, or null for the home view */
  value: ActivityOrganizationRow | null;
  onChange: (org: ActivityOrganizationRow | null) => void;
  homeLabel: string;
}

/** Rows rendered at once. Typing narrows; the tail says what is hidden. */
const MAX_VISIBLE_ROWS = 60;

const OrgLookup: React.FC<Props> = ({ organizations, value, onChange, homeLabel }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    // Search-first: opening the panel puts the caret in the box.
    const focusTimer = setTimeout(() => searchRef.current?.focus(), 0);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(focusTimer);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return organizations;
    const hits = organizations.filter(
      (o) => o.displayName.toLowerCase().includes(q) || o.accountId.toLowerCase().includes(q)
    );
    // A name that STARTS with what you typed is what you meant — surface those
    // first, the rest keep their alphabetical order underneath.
    return hits.sort((a, b) => {
      const aStarts = a.displayName.toLowerCase().startsWith(q) ? 0 : 1;
      const bStarts = b.displayName.toLowerCase().startsWith(q) ? 0 : 1;
      return aStarts - bStarts;
    });
  }, [organizations, query]);

  const visible = useMemo(() => filtered.slice(0, MAX_VISIBLE_ROWS), [filtered]);
  const hiddenCount = filtered.length - visible.length;

  // Reset the keyboard cursor whenever the result set changes under it.
  useEffect(() => {
    setCursor(0);
  }, [query, open]);

  const pick = (org: ActivityOrganizationRow | null) => {
    onChange(org);
    setOpen(false);
    setQuery('');
  };

  // ↑/↓ move, Enter picks, Escape closes (the panel's own Escape listener).
  const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, Math.max(visible.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const chosen = visible[cursor];
      if (chosen) pick(chosen);
    }
  };

  return (
    <Wrap ref={wrapRef}>
      <TriggerChip
        type="button"
        $open={open}
        $active={value !== null}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        title="view another organization's activity (activity page only)"
      >
        <span>{value ? `viewing: ${value.displayName}` : 'view another org'}</span>
        <Caret $open={open}>▼</Caret>
      </TriggerChip>
      {open && (
        <Panel role="listbox" aria-label="choose an organization to view">
          {/* Always present: this list is every org on the platform. */}
          <SearchInput
            ref={searchRef}
            type="search"
            placeholder="search all organizations by name or id…"
            aria-label="search organizations"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onSearchKeyDown}
          />
          <CountNote>
            {query.trim()
              ? `${filtered.length} of ${organizations.length} organization${organizations.length === 1 ? '' : 's'}`
              : `${organizations.length} organization${organizations.length === 1 ? '' : 's'}`}
          </CountNote>
          <Row
            type="button"
            role="option"
            aria-selected={value === null}
            $selected={value === null}
            onClick={() => pick(null)}
          >
            <RowLabel>{homeLabel}</RowLabel>
          </Row>
          {visible.map((org, i) => (
            <Row
              key={org.accountId}
              type="button"
              role="option"
              aria-selected={value?.accountId === org.accountId}
              $selected={value?.accountId === org.accountId}
              $cursor={i === cursor}
              onMouseEnter={() => setCursor(i)}
              onClick={() => pick(org)}
              title={org.accountId}
            >
              <RowLabel>{org.displayName}</RowLabel>
            </Row>
          ))}
          {hiddenCount > 0 && (
            <QuietNote>
              {`+${hiddenCount} more — keep typing to narrow`}
            </QuietNote>
          )}
          {organizations.length === 0 ? (
            <QuietNote>no other organizations to show yet.</QuietNote>
          ) : (
            filtered.length === 0 && <QuietNote>no organization matches that search.</QuietNote>
          )}
        </Panel>
      )}
    </Wrap>
  );
};

export default OrgLookup;
